'use client';

import { useTemporaryBookmarks } from '@/hooks/useTemporaryBookmarks';
import { MockDataMarker } from '@/lib/utils/mockDataMarker';
import { cssVars } from '@/styles/theme';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, Heart, Star } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';

interface Service {
  id: string;
  title: string;
  description: string;
  price: number;
  deliveryTime: number;
  rating?: number;
  reviewsCount?: number;
  image?: string;
  supplier: {
    id: string;
    name: string;
    avatar?: string;
    isVerified?: boolean;
  };
}

interface ServiceCardProps {
  service: Service;
  index?: number;
  isMockData?: boolean;
}

export default function ServiceCard({ service, index = 0, isMockData = false }: ServiceCardProps) {
  const locale = useLocale();
  const t = useTranslations('common.serviceCard');
  const tServices = useTranslations('pages.services');
  const { isBookmarked, toggleBookmark, isClient } = useTemporaryBookmarks();
  const bookmarked = isClient && isBookmarked(service.id, 'service');

  const handleBookmarkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleBookmark(service.id, 'service');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group relative overflow-hidden rounded-3xl border-2 shadow-lg transition-all hover:shadow-2xl"
      style={{
        backgroundColor: cssVars.neutral.surface,
        borderColor: cssVars.neutral.border,
      }}
    >
      <Link href={`/${locale}/services/${service.id}`} className="block">
        {/* Service Image */}
        {service.image && (
          <div className="relative h-48 w-full overflow-hidden">
            <img
              src={service.image}
              alt={service.title}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
            {/* Bookmark Button */}
            <button
              onClick={handleBookmarkClick}
              className="absolute end-4 top-4 rounded-full p-2 backdrop-blur-md transition-all hover:scale-110"
              style={{
                backgroundColor: bookmarked
                  ? cssVars.accent.warm
                  : `color-mix(in srgb, ${cssVars.neutral.white} 90%, transparent)`,
              }}
              aria-label={bookmarked ? tServices('bookmarkRemove') : tServices('bookmarkAdd')}
            >
              <Heart
                className="h-5 w-5"
                style={{
                  color: bookmarked ? cssVars.neutral.white : cssVars.neutral.textSecondary,
                  fill: bookmarked ? cssVars.neutral.white : 'none',
                }}
              />
            </button>
          </div>
        )}

        <div className="p-6">
          {/* Title */}
          <h3
            className="mb-2 line-clamp-2 text-xl font-bold transition-colors group-hover:opacity-80"
            style={{ color: cssVars.secondary.DEFAULT }}
          >
            {isMockData ? <MockDataMarker>{service.title}</MockDataMarker> : service.title}
          </h3>

          {/* Description */}
          <p className="mb-4 line-clamp-2 text-sm" style={{ color: cssVars.neutral.textSecondary }}>
            {isMockData ? (
              <MockDataMarker>{service.description}</MockDataMarker>
            ) : (
              service.description
            )}
          </p>

          {/* Supplier Info */}
          <div className="mb-4 flex items-center gap-2">
            {service.supplier.avatar && (
              <img
                src={service.supplier.avatar}
                alt={service.supplier.name}
                className="h-8 w-8 rounded-full object-cover"
              />
            )}
            <span className="text-sm font-medium" style={{ color: cssVars.neutral.text }}>
              {isMockData ? (
                <MockDataMarker>{service.supplier.name}</MockDataMarker>
              ) : (
                service.supplier.name
              )}
            </span>
            {service.supplier.isVerified && (
              <span className="text-xs font-semibold" style={{ color: cssVars.status.success }}>
                ✓
              </span>
            )}
          </div>

          {/* Rating & Reviews */}
          {service.rating !== undefined && (
            <div className="mb-4 flex items-center gap-1">
              <Star className="h-4 w-4 fill-current" style={{ color: cssVars.accent.warm }} />
              <span className="text-sm font-semibold" style={{ color: cssVars.neutral.text }}>
                {service.rating.toFixed(1)}
              </span>
              {service.reviewsCount !== undefined && (
                <span className="text-sm" style={{ color: cssVars.neutral.textSecondary }}>
                  ({service.reviewsCount})
                </span>
              )}
            </div>
          )}

          {/* Footer: Price & Delivery Time */}
          <div
            className="flex items-center justify-between border-t pt-4"
            style={{ borderColor: cssVars.neutral.border }}
          >
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" style={{ color: cssVars.neutral.textSecondary }} />
              <span className="text-sm" style={{ color: cssVars.neutral.textSecondary }}>
                {t('deliveryTime', { days: service.deliveryTime })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold" style={{ color: cssVars.primary.DEFAULT }}>
                {service.price}
              </span>
              <span className="text-sm" style={{ color: cssVars.neutral.textSecondary }}>
                {t('currency')}
              </span>
            </div>
          </div>

          {/* View Details Arrow */}
          <div className="mt-4 flex items-center justify-end gap-2">
            <span className="text-sm font-semibold" style={{ color: cssVars.primary.DEFAULT }}>
              {t('viewDetails')}
            </span>
            <ArrowRight
              className="h-5 w-5 transition-transform group-hover:translate-x-1"
              style={{ color: cssVars.primary.DEFAULT }}
            />
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
